import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosPromise } from 'axios';
import CommonGrid from 'shared/commonGrid';
import {
  SelectionChangedEvent,
  ColDef,
  ColGroupDef
} from '@ag-grid-enterprise/all-modules';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { Contact, ContactGridData } from 'data/contact/contact.types';
import { ServerResponse, UserSettings, GridSettings } from 'data/types';
import isTestEnvironment from 'utils/environment/isTestEnvironment';
import './contact-grid.scss';
import ContactListMenu from '../ContactListMenu';

type Props = {
  selectedContact?: Contact;
  data: ContactGridData[];
  hasPrivilegesToCreate: boolean;
  hasPrivilegeToDelete: boolean;
  hasPrivilegeToView?: boolean;
  noResults: boolean;
  beforeLogout: boolean;
  lastUpdatedAt: Date | undefined;
  columnDefs: (ColGroupDef | ColDef)[];
  columnDefsVersion: string;
  suppressColumnVirtualisation?: boolean;
  suppressAggFuncInHeader: boolean;
  groupSelectsChildren: boolean;
  suppressRowClickSelection: boolean;
  enableGroupSelection: boolean;
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCreateNew: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSelectionChanged: (event: SelectionChangedEvent) => void;
  onSaveGridSettings: (gridSettings: GridSettings) => void;
  onGetGridSettings: () => {
    type: string;
    payload: AxiosPromise<ServerResponse<UserSettings>>;
  };
  onEdit: () => void;
  onDelete: () => void;
  onSelectItemMenuAction: (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void;
};

const ContactGridGrid: FunctionComponent<Props> = ({
  hasPrivilegesToCreate,
  hasPrivilegeToDelete,
  hasPrivilegeToView = true,
  data = [],
  noResults,
  beforeLogout,
  lastUpdatedAt,
  columnDefs,
  columnDefsVersion,
  suppressColumnVirtualisation = isTestEnvironment(),
  selectedContact,
  suppressAggFuncInHeader,
  groupSelectsChildren,
  suppressRowClickSelection,
  enableGroupSelection,
  onRefresh,
  onCreateNew,
  onSelectionChanged,
  onSaveGridSettings,
  onGetGridSettings,
  onEdit,
  onDelete,
  onSelectItemMenuAction
}: Props) => {
  const { t } = useTranslation();

  return (
    <div
      className="grid-container grid-group-header-container contact-grid"
      style={{ height: 'inherit' }}
    >
      <CommonGrid
        createNewText={t('contact.grid.createNew')}
        pagination
        enableBrowserTooltips
        rowSelection={ROWSELECTION.SINGLE}
        hasPrivilegeToCreateNew={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegetoView={hasPrivilegeToView}
        rowData={data}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        columnDefs={columnDefs}
        columnDefsVersion={columnDefsVersion}
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        lastUpdatedAt={lastUpdatedAt}
        handleCreateNew={onCreateNew}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        saveGridSettings={onSaveGridSettings}
        getGridSettingsFromServer={onGetGridSettings}
        suppressAggFuncInHeader={suppressAggFuncInHeader}
        groupSelectsChildren={groupSelectsChildren}
        suppressRowClickSelection={suppressRowClickSelection}
        enableGroupSelection={enableGroupSelection}
        onActionMenuItemClick={onSelectItemMenuAction}
      >
        <ContactListMenu
          selectedContact={selectedContact}
          hasPrivilegesToCreate={hasPrivilegesToCreate}
          hasPrivilegeToDelete={hasPrivilegeToDelete}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CommonGrid>
    </div>
  );
};

export default ContactGridGrid;
